import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'antd';
import cn from 'classnames';

import EPTierLock from '@/comp/common/EPTierLock';
import EPTierProtection from '@/comp/common/EPTierProtection';
import { ComponentTierName, interestedPackageEnum } from '@/constants/enums';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import LockIcon from '@/comp/common/icons/LockIcon';
import FilledArrowIcon from '@/comp/common/icons/FilledArrowIcon';

import { TargetTooltipPopoverStyle, Container, Point } from './DetailsWidgetSlider.styles';
import { DetailsWidgetSliderProps, SliderPoint } from './DetailsWidgetSlider.types';
import { DeviceType } from '@/constants/deviceTypeEnum';

const DetailsWidgetSlider = observer(
  ({
    title,
    titleSuffix,
    min,
    max,
    points,
    value,
    unit,
    connectivity,
    highlightSlider,
    addSideGaps = false,
    showTierLock = false,
    hasTarget = false,
    deviceType,
    isInMaintenance,
  }: DetailsWidgetSliderProps) => {
    const { tcvalue } = useValueByConnectivity();
    const { t } = useTranslation();

    let adjustedMin = !isNaN(min) && min != null ? min : max != null ? max * 0.9 : null;
    let adjustedMax = !isNaN(max) && max != null ? max : min != null ? min * 1.1 : null;
    if (deviceType === DeviceType.DesiccantAirDryer) {
      adjustedMin = -80;
      adjustedMax = 0;
    }
    const targetPoint = points.find(point => point.isTarget);

    const renderPoints = (points: SliderPoint[]) => {
      const renderedValues = new Map();
      const labelOffsets = new Map();
      return points.map(point => {
        if (point.value == null || isNaN(point.value)) return null;

        const existingPoint = renderedValues.get(point.value);
        const offset = labelOffsets.get(point.value) || 0;
        renderedValues.set(point.value, (existingPoint || 0) + 1);
        labelOffsets.set(point.value, offset + 1);
        const isSubsequentPoint = existingPoint !== undefined;

        return (
          <Point
            key={`${point.value}-${renderedValues.get(point.value)}`}
            $value={point.value}
            $highlighted={point.highlighted}
            $min={adjustedMin}
            $max={adjustedMax}
            $addSideGaps={addSideGaps}
          >
            {point.isTarget ? (
              <Popover placement="bottom" content={point.value} overlayClassName="target-tooltip">
                <TargetTooltipPopoverStyle />
                <div className="target" />
              </Popover>
            ) : isSubsequentPoint ? (
              <div className="subsequent-label" style={{ top: `${offset * 30}px` }}>
                <div className="label">{point.label}</div>
              </div>
            ) : (
              <>
                <div className="pipe" />
                <div>
                  <div className="point-value">{point.value}</div>
                  <div className="label">{point.label}</div>
                </div>
              </>
            )}
          </Point>
        );
      });
    };

    return (
      <Container
        $min={adjustedMin}
        $max={adjustedMax}
        $value={value}
        $highlightSlider={highlightSlider}
        $addSideGaps={addSideGaps}
        className="details-widget-slider"
        $unit={unit}
        $isInMaintenance={isInMaintenance}
      >
        <div className="title-wrapper-tier">
          <div>
            {titleSuffix && <span className="title-suffix">{titleSuffix}</span>}
            {title && <span className="title">{title}</span>}
          </div>

          {showTierLock && (
            <EPTierProtection componentName={ComponentTierName.CompressorModalLockButton}>
              <div className="lock-wrapper">
                <EPTierLock
                  label={t('enableSystemControl')}
                  tooltipTextPart2={t('alwaysPerform')}
                  icon={<LockIcon />}
                  tooltipWidth={300}
                  emailRequestTypeOfInterestedPackage={interestedPackageEnum.SYSTEM_CONTROL}
                  emailRequestButtonClicked={'Lock Button'}
                  emailRequestWhichScreen={'Compressor Details'}
                />
              </div>
            </EPTierProtection>
          )}
        </div>

        {hasTarget && (
          <div className={cn('target-value', !targetPoint?.value && 'target-value-hidden')}>
            <span className="target-value-label">{t(targetPoint?.label)}:&nbsp;</span>
            <span className="target-value-value">{targetPoint?.value}</span>
            <span className="target-value-unit"> {unit}</span>
          </div>
        )}

        <div className="slider-container">
          <div className="value-container">
            <div className="value-arrow-block">
              <div className="value">
                <span className="value-content"> {tcvalue(value, connectivity)} </span>
                <span className="units-sign"> {value != null && !isNaN(value) && unit} </span>
              </div>
              <div className="triangle">
                <FilledArrowIcon />
              </div>
            </div>
          </div>

          <div className="slider">
            <div className="filler" />
            <div className="grey-filler" />
          </div>

          <div className="points">{renderPoints(points)}</div>
        </div>
      </Container>
    );
  },
);

export default DetailsWidgetSlider;
